import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxDatepickerComponent,
  OnyxSectionComponent,
  OnyxTabsComponent,
  OnyxToggleComponent,
} from '@onyx/angular';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { ValidationGroup } from '../../../../common/interfaces/validation/validation-group';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import { TachographMake } from '../../common/enums/tachograph-make';
import { TachographModel } from '../../common/enums/tachograph-model';

export type FleetTachographFormGroup = ReturnType<
  typeof FleetTachographFormComponent.buildForm
>;

@Component({
  selector: 'app-fleet-tachograph-form',
  imports: [
    OnyxSectionComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxToggleComponent,
    AsyncPipe,
    OnyxDatepickerComponent,
    OnyxTabsComponent,
  ],
  templateUrl: './fleet-tachograph-form.component.html',
  styleUrl: './fleet-tachograph-form.component.scss',
})
export class FleetTachographFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);

  public readonly form = input.required<FleetTachographFormGroup>();
  public readonly validation = input.required<ValidationGroup>();

  protected readonly I18N = 'fleet.fleetForm.tachograph';

  protected models$ = this.dictionariesService.getDictionary(
    DictionaryCode.TACHOGRAPH_MODEL,
  );
  protected makes$ = this.dictionariesService.getDictionary(
    DictionaryCode.TACHOGRAPH_MAKE,
  );

  protected isOptional = computed(() => !this.validation().required);
  protected showForm = signal(false);

  public ngOnInit() {
    this.showForm.set(!!this.form().getRawValue().nextLegalizationDate);

    effect(
      () => {
        const showForm = this.showForm() || !this.isOptional();
        ValidationHelper.toggleControls(this.form(), {
          enabled: showForm,
          reset: true,
        });
      },
      { injector: this.injector },
    );
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      model: fb.control(TachographModel.MODEL_4_1, [Validators.required]),
      make: fb.control(TachographMake.VDO, [Validators.required]),
      nextLegalizationDate: fb.control<string | null>(null, [
        Validators.required,
      ]),
    });
  }
}
